// Detail, přesun a zrušení zkušebního tréninku (mockup v8, obrazovky 8 a 9).
import React, { useEffect, useState } from "react";
import { CalendarPlus, CalendarClock, CircleX, Phone, ArrowUpRight } from "lucide-react";
import { Modal, HlavickaOkna, Field } from "../ui.jsx";
import DatePicker from "../DatePicker.jsx";
import { toast } from "../Toast.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { salesGet, salesPost, denKratce, dm, casBezNuly, dnesIso, addDays, telefonHezky } from "./salesApi.js";
import { Oznameni, RadekShrnuti, Volby, Fajfky } from "./salesUi.jsx";
import TerminPicker from "./TerminPicker.jsx";
import { NedorazilaDialog } from "../deal/DealDialogy.jsx";
import { VyberCasu } from "./Dialogy.jsx";

function VysledekKroku({ kroky }) {
  const spatne = (kroky || []).filter((k) => !k.ok);
  if (!spatne.length) return null;
  return (
    <Oznameni ton="pozor">
      Něco neprošlo:
      <ul className="mt-1 list-disc pl-5">{spatne.map((k, i) => <li key={i}>{k.text || k.krok}{k.chyba ? `: ${k.chyba}` : ""}</li>)}</ul>
    </Oznameni>
  );
}

export function ZkusebniBanner({ z }) {
  if (!z) return null;
  if (z.stav === "zruseno") return <Oznameni ton="info" ikona={CircleX}><b>Zkušební {denKratce(z.datum)} v {casBezNuly(z.cas)} je zrušený.</b>{z.zruseno_duvod ? ` Důvod: ${z.zruseno_duvod}.` : ""}</Oznameni>;
  if (!z.zaplaceno && z.dni_po_splatnosti >= 2 && !z.probehl) return <Oznameni ton="problem"><b>{z.dni_po_splatnosti}. den po splatnosti.</b> SMS o platbě odešla automaticky. Zavolej, domluv platbu ještě dnes, jinak zkušební trénink zruš.</Oznameni>;
  if (!z.zaplaceno && z.dni_po_splatnosti === 1 && !z.probehl) return <Oznameni ton="pozor"><b>1. den po splatnosti.</b> Dnes jí automaticky odešla SMS o platbě. Zítra jí zavolej, pokud nezaplatí.</Oznameni>;
  return null;
}

export function ZkusebniDetailDialog({ z, open, onClose, onPresun, onZrusit, onKarta }) {
  const uzka = useUzkaObrazovka();
  if (!z) return null;
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarPlus} nazev={z.jmeno} popis="Zkušební trénink" onClose={onClose} />}>
      <div className="space-y-4">
        <ZkusebniBanner z={z} />
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-1">
          <RadekShrnuti popisek="Termín">{denKratce(z.datum)}, {casBezNuly(z.cas)} až {casBezNuly(z.konec)}</RadekShrnuti>
          <RadekShrnuti popisek="Trenér">{z.trener}</RadekShrnuti>
          <RadekShrnuti popisek="Faktura">{z.faktura ? `${z.faktura}, ${z.castka} Kč${z.splatnost ? `, splatnost ${dm(z.splatnost)}` : ""}` : "bez faktury"}{z.zaplaceno ? ", zaplaceno" : ""}</RadekShrnuti>
          {z.telefon && <RadekShrnuti popisek="Telefon">{telefonHezky(z.telefon)}</RadekShrnuti>}
          {z.pravidelne && <RadekShrnuti popisek="Pravidelně">{z.pravidelne}</RadekShrnuti>}
        </div>
        {uzka && z.telefon && <a href={`tel:${String(z.telefon).replace(/\s/g, "")}`} className="btn-primary w-full !min-h-[52px]"><Phone className="h-5 w-5" />Zavolat {telefonHezky(z.telefon)}</a>}
        {z.stav === "aktivni" && !z.probehl && (
          <div className="grid gap-2 sm:grid-cols-3">
            <button type="button" className="btn-secondary !min-h-[52px]" onClick={onPresun}><CalendarClock className="h-4 w-4" />Přesunout termín</button>
            <button type="button" className="btn-secondary !min-h-[52px]" onClick={onKarta}><ArrowUpRight className="h-4 w-4" />Otevřít kartu</button>
            <button type="button" className="btn border border-rose-300 bg-white text-rose-700 !min-h-[52px]" onClick={onZrusit}><CircleX className="h-4 w-4" />Zrušit zkušební</button>
          </div>
        )}
        {(z.stav !== "aktivni" || z.probehl) && <button type="button" className="btn-secondary w-full" onClick={onKarta}><ArrowUpRight className="h-4 w-4" />Otevřít kartu</button>}
      </div>
    </Modal>
  );
}

export function PresunDialog({ z, open, onClose, onHotovo }) {
  const [termin, setTermin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [vysledek, setVysledek] = useState(null);
  useEffect(() => { if (open) { setTermin(null); setChyba(""); setVysledek(null); } }, [open]);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("presunout", { zkusebni_id: z.id, trener_id: termin.trener_id, datum: termin.datum, cas: termin.cas });
      setVysledek(r);
      toast("Termín přesunut.", "success");
      onHotovo?.(r);
      if (!(r.kroky || []).some((k) => !k.ok)) onClose();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!z) return null;
  const jinyTrener = termin && String(termin.trener_id) !== String(z.trener_id);
  return (
    <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka
      header={<HlavickaOkna ikona={CalendarClock} nazev="Přesunout zkušební trénink" popis={z.jmeno} onClose={onClose} />}
      footer={vysledek ? <button type="button" className="btn-primary" onClick={onClose}>Hotovo</button> : <>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !termin} aria-busy={busy}>Uložit nový termín</button>
      </>}>
      <div className="space-y-3">
        {vysledek ? <VysledekKroku kroky={vysledek.kroky} /> : (
          <>
            <Oznameni ton="znacka">Původně <b>{denKratce(z.datum)} v {casBezNuly(z.cas)} s trenérem {z.trener}</b>. Vyber nový termín, trenér se může změnit.</Oznameni>
            {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
            <TerminPicker trenerVychozi="" value={termin} onChange={setTermin} zacitPristimTydnem={false} />
            <div className="space-y-2 rounded-2xl border border-brand-200 bg-brand-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-brand-deep">Po uložení se samo</div>
              <Fajfky polozky={[
                jinyTrener ? <>převede trénink v kalendáři trenérovi <b>{termin.trener}</b></> : <>přesune trénink v kalendáři</>,
                <>pošle do <b>Slacku</b> změnu termínu{jinyTrener ? <> a označí <b>oba trenéry</b></> : null}</>,
                <>pošle klientce <b>e‑mail s novým termínem</b> a pozvánku do kalendáře</>,
                <>faktura zůstává stejná</>,
              ]} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// 6. 10. 2026 (Láďa, schválený návrh 8): zkušební převzatý z kalendáře (Reservio, trenér,
// web) nemá fakturu. Sales ji vystaví tady, stejná faktura a hlídání platby jako u
// zkušebního domluveného přes sales. Jiná možnost než faktura není.
export function FakturaDialog({ z, open, onClose, onHotovo }) {
  const [email, setEmail] = useState("");
  const [f, setF] = useState({ typ: "osoba", ulice: "", mesto: "", psc: "", firma: "", ico: "", dic: "" });
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [predvyplneno, setPredvyplneno] = useState(null);
  // 7. 10. 2026 (Láďa): klientka se zkušebním už nějakou fakturu měla, adresa se předvyplní
  // (sales_info.fakturace, jinak poslední faktura v PG). Sales ji jen zkontroluje.
  useEffect(() => {
    if (!open) return;
    setEmail(z?.email || ""); setChyba(""); setPredvyplneno(null);
    setF({ typ: "osoba", ulice: "", mesto: "", psc: "", firma: "", ico: "", dic: "" });
    let zije = true;
    salesGet("fakturace", { zkusebni_id: z?.id }).then((r) => {
      if (!zije || !r?.fakturace) return;
      const a = r.fakturace;
      setF({ typ: a.typ === "firma" ? "firma" : "osoba", ulice: a.ulice || "", mesto: a.mesto || "", psc: a.psc || "", firma: a.firma || "", ico: a.ico || "", dic: a.dic || "" });
      setPredvyplneno(r.zdroj?.text || "předvyplněná");
    }).catch(() => {});
    return () => { zije = false; };
  }, [open, z]);
  const nast = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const chybi = [
    !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim()) && "e‑mail",
    f.typ === "firma" ? (!(f.ico && f.firma) && "IČO a název firmy") : (!(f.ulice && f.mesto && f.psc) && "adresa (ulice, město, PSČ)"),
  ].filter(Boolean);
  const uloz = async () => {
    if (chybi.length) { setChyba(`Ještě chybí: ${chybi.join(", ")}.`); return; }
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("zkusebni_faktura", { zkusebni_id: z.id, email: String(email).trim(), fakturace: f });
      toast(`Faktura ${r.faktura} vystavena a odeslána.`, "success");
      onHotovo?.(r); onClose();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!z) return null;
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarPlus} nazev="Vystavit fakturu za zkušební" popis={`${z.jmeno}, ${denKratce(z.datum)} v ${casBezNuly(z.cas)}`} onClose={onClose} />}
      footer={<>
        {chyba && <div className="basis-full"><Oznameni ton="problem">{chyba}</Oznameni></div>}
        {!chyba && chybi.length > 0 && <div className="basis-full text-[13px] font-semibold text-slate-500">Ještě chybí: {chybi.join(", ")}.</div>}
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || chybi.length > 0} aria-busy={busy}>{busy ? "Vystavuju…" : "Vystavit a poslat"}</button>
      </>}>
      <div className="space-y-3">
        <Oznameni ton="znacka">Faktura {z.castka ? `${z.castka} Kč` : ""} se pošle e‑mailem a platba se hlídá jako u ostatních zkušebních. Když klientka později termín přesune, faktura zůstává.</Oznameni>
        <Field label="E‑mail klientky"><input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></Field>
        <Volby moznosti={[{ key: "osoba", label: "Na osobu" }, { key: "firma", label: "Na firmu" }]} value={f.typ} onChange={(v) => nast("typ", v)} />
        {predvyplneno && <Oznameni ton="ok">Adresa {predvyplneno}, jen ji zkontroluj.</Oznameni>}
        {f.typ === "firma" && (
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="IČO"><input className="field" inputMode="numeric" value={f.ico} onChange={(e) => nast("ico", e.target.value.replace(/\D/g, "").slice(0, 8))} /></Field>
            <Field label="Firma"><input className="field" value={f.firma} onChange={(e) => nast("firma", e.target.value)} /></Field>
          </div>
        )}
        <Field label="Ulice a číslo"><input className="field" value={f.ulice} onChange={(e) => nast("ulice", e.target.value)} autoComplete="off" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Město"><input className="field" value={f.mesto} onChange={(e) => nast("mesto", e.target.value)} /></Field>
          <Field label="PSČ"><input className="field" inputMode="numeric" value={f.psc} onChange={(e) => nast("psc", e.target.value)} /></Field>
        </div>
        {f.typ === "firma" && <Field label="DIČ"><input className="field" value={f.dic} onChange={(e) => nast("dic", e.target.value)} /></Field>}
      </div>
    </Modal>
  );
}

const DUVODY = [
  { key: "neuhrazeno", label: "Nezaplatila fakturu" }, { key: "klientka", label: "Zrušila sama" },
  { key: "nemoc", label: "Nemoc nebo jiná překážka" }, { key: "jiny", label: "Jiný důvod" },
];
const DAL = [
  { key: "nqlead", label: "Uzavřít jako NQlead" }, { key: "jindy", label: "Zavolat jindy a domluvit nový termín" },
];
// 22. 10. 2026 (Láďa): méně než 24 hodin před začátkem se nejdřív ptá, jestli zrušila včas.
// Pozdní zrušení se nezapisuje jako zrušení, ale jako Nedorazila na zkušební (zůstane propadlý, příště platí nový).
const KDY_ZRUSILA = [
  { key: "vcas", label: "Víc než 24 hodin předem", popis: "Dala vědět včas, jen se to zapisuje až teď. Zkušební se normálně zruší." },
  { key: "pozde", label: "Méně než 24 hodin předem", popis: "Zapíše se Nedorazila na zkušební. Zkušební zůstane jako propadlý a nový zaplatí znovu." },
];
export function ZrusitDialog({ z, open, onClose, onHotovo }) {
  const [kdy, setKdy] = useState("");
  const [nedorazila, setNedorazila] = useState(false);
  const [duvod, setDuvod] = useState("");
  const [text, setText] = useState("");
  const [dal, setDal] = useState("nqlead");
  const [datum, setDatum] = useState("");
  const [cas, setCas] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [vysledek, setVysledek] = useState(null);
  useEffect(() => { if (open) { setKdy(""); setNedorazila(false); setDuvod(z && !z.zaplaceno && z.dni_po_splatnosti >= 1 ? "neuhrazeno" : ""); setText(""); setDal("nqlead"); setDatum(addDays(dnesIso(), 1)); setCas(""); setChyba(""); setVysledek(null); } }, [open, z]);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("zrusit", { zkusebni_id: z.id, duvod, text, dal, datum, cas, vcas: z.mene_24h ? kdy === "vcas" : undefined });
      setVysledek(r);
      toast("Zkušební trénink zrušen.", "success");
      onHotovo?.(r);
      if (!(r.kroky || []).some((k) => !k.ok)) onClose();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!z) return null;
  if (open && nedorazila) {
    return <NedorazilaDialog open polozka={{ id: z.id, jmeno: z.jmeno, neuhrazeno: !!z.neuhrazeno }} onClose={() => setNedorazila(false)}
      uloz={(telo) => salesPost("nedorazila", { ...telo, lead_id: z.lead_id })}
      onHotovo={async (r) => { await onHotovo?.(r); toast("Zapsáno: nedorazila na zkušební.", "success"); onClose(); }} />;
  }
  const ptat = !!z.mene_24h;
  const pozde = ptat && kdy === "pozde";
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={CircleX} nazev="Zrušit zkušební trénink" popis={`${z.jmeno}, ${denKratce(z.datum)} v ${casBezNuly(z.cas)}`} onClose={onClose} />}
      footer={vysledek ? <button type="button" className="btn-primary" onClick={onClose}>Hotovo</button> : <>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        {pozde
          ? <button type="button" className="btn-primary" onClick={() => setNedorazila(true)} disabled={!z.lze_nedorazila}>Zapsat Nedorazila</button>
          : <button type="button" className="btn-danger" onClick={uloz} disabled={busy || !duvod || (ptat && !kdy)} aria-busy={busy}>Zrušit trénink</button>}
      </>}>
      <div className="space-y-4">
        {vysledek ? <VysledekKroku kroky={vysledek.kroky} /> : (
          <>
            {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
            {ptat && <div><span className="label">Do začátku zbývá méně než 24 hodin. Kdy to klientka zrušila?</span><Volby moznosti={KDY_ZRUSILA} value={kdy} onChange={setKdy} name="Kdy zrušila" /></div>}
            {pozde && (z.lze_nedorazila
              ? <Oznameni ton="pozor"><b>Pozdní zrušení se zapisuje jako Nedorazila na zkušební.</b> V dalším kroku vybereš, jestli se omluvila.</Oznameni>
              : <Oznameni ton="problem">Zkušební je starší než 60 dní, Nedorazila už zapsat nejde. Napiš Kristině.</Oznameni>)}
            {(!ptat || kdy === "vcas") && <>
            <div><span className="label">Důvod</span><Volby moznosti={DUVODY} value={duvod} onChange={setDuvod} name="Důvod" /></div>
            {duvod === "jiny" && <Field label="Jaký důvod"><input className="field" value={text} onChange={(e) => setText(e.target.value)} /></Field>}
            <div><span className="label">Co s kontaktem dál</span><Volby moznosti={DAL} value={dal} onChange={setDal} name="Co dál" /></div>
            {dal === "jindy" && (
              <div className="grid grid-cols-2 gap-3">
                <div><span className="label">Zavolat</span><DatePicker value={datum} min={dnesIso()} onChange={setDatum} /></div>
                <VyberCasu value={cas} onChange={setCas} />
              </div>
            )}
            <div className="space-y-2 rounded-2xl border border-rose-200 bg-rose-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Po potvrzení se samo</div>
              <Fajfky polozky={[
                <>zruší trénink v kalendáři trenéra</>,
                <>stornuje <b>fakturu</b> ve FAPI{z.zaplaceno ? " (je zaplacená, vrácení peněz vyřeš ručně)" : ""}</>,
                <>napíše do <b>Slacku</b> trenérovi, že trénink odpadá</>,
                <>pošle klientce <b>e‑mail o zrušení</b></>,
              ]} />
            </div>
            </>}
          </>
        )}
      </div>
    </Modal>
  );
}

export { VysledekKroku };
